import { useState } from 'react';
import { Mail, Building2, ShieldAlert, LifeBuoy, Check } from 'lucide-react';
import { Hero } from '@/components/marketing/primitives.jsx';

const inquiryTypes = [
  { value: 'agency_onboarding',   label: 'Agency onboarding' },
  { value: 'partner_integration', label: 'Partner integration' },
  { value: 'judicial',            label: 'Judicial / warrant process' },
  { value: 'press',               label: 'Press inquiry' },
  { value: 'privacy',             label: 'Privacy inquiry' },
  { value: 'security',            label: 'Security / responsible disclosure' },
  { value: 'oversight',           label: 'Oversight / IG inquiry' },
  { value: 'other',                label: 'Other' },
];

const channels = [
  {
    icon: Building2,
    title: 'Partnerships',
    description: 'For agencies, institutions, election authorities, and program offices exploring integration.',
    contact: 'partnerships@nacis.example',
  },
  {
    icon: ShieldAlert,
    title: 'Security disclosures',
    description: 'Authorised security research, vulnerability reports, and responsible disclosure.',
    contact: 'security@nacis.example',
  },
  {
    icon: LifeBuoy,
    title: 'Citizen support',
    description: 'App issues, account recovery, report status questions, and identity challenge help.',
    contact: 'help@nacis.example',
  },
  {
    icon: Mail,
    title: 'Press & oversight',
    description: 'Media inquiries and communications with the Inspector General office.',
    contact: 'press@nacis.example',
  },
];

export default function Contact() {
  const [form, setForm] = useState({
    name: '', email: '', organisation: '', inquiry_type: 'agency_onboarding', message: '',
  });
  const [submitted, setSubmitted] = useState(false);
  const [errors, setErrors] = useState({});

  function update(field, value) {
    setForm(f => ({ ...f, [field]: value }));
    if (errors[field]) setErrors(e => ({ ...e, [field]: null }));
  }

  function validate() {
    const e = {};
    if (!form.name.trim())  e.name  = 'Please tell us your name.';
    if (!form.email.trim()) e.email = 'We need an email to reply.';
    else if (!/^\S+@\S+\.\S+$/.test(form.email)) e.email = 'That email looks off — double-check it.';
    if (form.message.trim().length < 20) e.message = 'Tell us a bit more — at least 20 characters.';
    setErrors(e);
    return Object.keys(e).length === 0;
  }

  function onSubmit(ev) {
    ev.preventDefault();
    if (!validate()) return;
    // Submission is wired to a backend endpoint in a follow-up slice.
    // For now, hand off to the appropriate mail channel so nothing is lost.
    setSubmitted(true);
  }

  return (
    <>
      <Hero
        eyebrow="Contact"
        title="We read every message."
        description="Pick the right channel below, or use the form and we will route your message internally."
      />

      <section className="py-20 md:py-24">
        <div className="container-wide">
          <div className="grid gap-8 lg:grid-cols-2 lg:gap-16">
            <div>
              <h2 className="text-2xl font-semibold text-slate-900">Direct channels</h2>
              <p className="mt-3 text-base text-slate-600">
                For the fastest response, reach out through the channel that matches your purpose. Partnership inquiries
                are handled within 2 business days; security disclosures within 3.
              </p>
              <div className="mt-8 space-y-4">
                {channels.map(c => {
                  const Icon = c.icon;
                  return (
                    <div key={c.title} className="flex gap-4 rounded-xl border border-slate-200 bg-white p-5">
                      <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-lg bg-sky-50 text-sky-700">
                        <Icon className="h-5 w-5" strokeWidth={2} />
                      </div>
                      <div>
                        <div className="font-semibold text-slate-900">{c.title}</div>
                        <p className="mt-1 text-sm text-slate-600">{c.description}</p>
                        <div className="mt-2 font-mono text-sm text-sky-700">{c.contact}</div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            <div className="rounded-2xl border border-slate-200 bg-white p-6 md:p-8">
              {submitted ? (
                <div className="flex h-full flex-col items-start justify-center">
                  <div className="flex h-12 w-12 items-center justify-center rounded-full bg-emerald-50 text-emerald-600">
                    <Check className="h-6 w-6" />
                  </div>
                  <h3 className="mt-6 text-xl font-semibold text-slate-900">Thanks — message received.</h3>
                  <p className="mt-3 text-slate-600">
                    We have recorded your note under the <em>{inquiryTypes.find(t => t.value === form.inquiry_type)?.label}</em>{' '}
                    channel. Expect a reply from the right team within 2–3 business days.
                  </p>
                  <button
                    type="button"
                    onClick={() => { setSubmitted(false); setForm({ name: '', email: '', organisation: '', inquiry_type: 'agency_onboarding', message: '' }); }}
                    className="btn-secondary mt-6"
                  >
                    Send another message
                  </button>
                </div>
              ) : (
                <form onSubmit={onSubmit} className="space-y-5" noValidate>
                  <h2 className="text-2xl font-semibold text-slate-900">Send us a message</h2>

                  <Field label="Your name" error={errors.name} htmlFor="contact-name">
                    <input
                      id="contact-name"
                      type="text"
                      value={form.name}
                      onChange={e => update('name', e.target.value)}
                      className="input"
                      autoComplete="name"
                    />
                  </Field>

                  <Field label="Email" error={errors.email} htmlFor="contact-email">
                    <input
                      id="contact-email"
                      type="email"
                      value={form.email}
                      onChange={e => update('email', e.target.value)}
                      className="input"
                      autoComplete="email"
                    />
                  </Field>

                  <Field label="Organisation (optional)" htmlFor="contact-org">
                    <input
                      id="contact-org"
                      type="text"
                      value={form.organisation}
                      onChange={e => update('organisation', e.target.value)}
                      className="input"
                      autoComplete="organization"
                    />
                  </Field>

                  <Field label="Inquiry type" htmlFor="contact-type">
                    <select
                      id="contact-type"
                      value={form.inquiry_type}
                      onChange={e => update('inquiry_type', e.target.value)}
                      className="input"
                    >
                      {inquiryTypes.map(t => (
                        <option key={t.value} value={t.value}>{t.label}</option>
                      ))}
                    </select>
                  </Field>

                  <Field label="Message" error={errors.message} htmlFor="contact-message">
                    <textarea
                      id="contact-message"
                      rows={6}
                      value={form.message}
                      onChange={e => update('message', e.target.value)}
                      className="input"
                    />
                  </Field>

                  <button type="submit" className="btn-primary w-full">
                    Send message
                  </button>
                  <p className="text-xs text-slate-500">
                    Do not submit classified or investigative material through this form. Use the Judicial process
                    channel for authorised requests.
                  </p>
                </form>
              )}
            </div>
          </div>
        </div>
      </section>
    </>
  );
}

function Field({ label, error, htmlFor, children }) {
  return (
    <div>
      <label htmlFor={htmlFor} className="block text-sm font-medium text-slate-800">
        {label}
      </label>
      <div className="mt-1">{children}</div>
      {error && <div className="mt-1 text-xs text-rose-600">{error}</div>}
    </div>
  );
}
